import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import {PaymentTerm} from "./models/payment-term.model";
import {SalesOrder, SalesOrderActionMetadata, SalesOrderLifecycleActionRequest} from "./sales-order/sales-order.model";
import {Products} from "./models/product.model";
import {APIListWrapper} from "../models/common/api-list-wrapper.model";
import {environment} from "../../environments/environment";
import {PurchaseOrder, PurchaseOrderActionMetadata} from "./purchase-order/purchase-order.model";
import {OrderActivity, OrderListRow, OrderSavedView, OrderSearchRequest, OrderSummary} from "./order-workbench.model";


import {SalesOrderAttachment} from "./sales-order/sales-order-attachment.model";
import { PurchaseOrderAttachment } from "./purchase-order/purchase-order-attachment.model";
import { PurchaseOrderReceipt } from "./purchase-order/purchase-order-receipt.model";
import { SalesOrderFromQuotationPreview, SalesOrderFromQuotationRequest, SalesOrderFromQuotationResult } from "./sales-order/sales-order-quotation-conversion.model";
import {
    FulfillmentReadinessResponse, CreateReleaseRequest, CreateReturnReleaseRequest,
    ReleaseStatusResponse, SalesOrderQuantityCorrectionPreview, SalesOrderQuantityCorrectionRequest,
    SalesOrderQuantityCorrectionResult
} from "./sales-order/sales-order-fulfillment.model";
import {
    InvoiceReadinessResponse, CreateInvoiceDraftRequest, InvoiceDraftResponse
} from "./sales-order/sales-order-invoice-readiness.model";
import {
    CreateVendorBillDraftRequest, PayableStagingResponse, VendorBillDraftResponse, VendorBillReadinessResponse
} from "./purchase-order/purchase-order-vendor-bill.model";
import { ExecutionDraftCreateRequest, ExecutionDraftCreateResponse, ExecutionDraftPreview, ExecutionDraftPreviewRequest, ExecutionOrderLineage } from './sales-order/execution-sales-order.model';

@Injectable({
    providedIn: 'root'
})
export class OrdersService {

    private baseUrl = `${environment.restApiUrl}orders`; // Your base URL for the SalesOrdersEndpoint

    constructor(private http: HttpClient) { }

    /**
     * Retrieves all sales orders.
     * @param owningCompanyId The ID of the owning company.
     * @param page The page number.
     * @param pageSize The number of items per page.
     * @returns An Observable of APIListWrapper<SalesOrder> object.
     */
    getAllSalesOrdersByOwningCompany(owningCompanyId: number, page: number, pageSize: number): Observable<APIListWrapper<SalesOrder>> {
        const url = `${this.baseUrl}/sales-orders`;
        let params = new HttpParams()
            .set('owningCompanyId', owningCompanyId.toString())
            .set('page', page.toString())
            .set('pageSize', pageSize.toString());
        return this.http.get<APIListWrapper<SalesOrder>>(url, { params });
    }

    searchSalesOrders(request: OrderSearchRequest): Observable<APIListWrapper<OrderListRow>> {
        const url = `${this.baseUrl}/sales-orders/search`;
        return this.http.post<APIListWrapper<OrderListRow>>(url, request);
    }

    getSalesOrderSummary(request: OrderSearchRequest): Observable<OrderSummary> {
        const url = `${this.baseUrl}/sales-orders/summary`;
        return this.http.post<OrderSummary>(url, request);
    }

    exportSalesOrders(request: OrderSearchRequest, format: 'xlsx' | 'csv' = 'xlsx'): Observable<Blob> {
        const url = `${this.baseUrl}/sales-orders/export`;
        const params = new HttpParams().set('format', format);
        return this.http.post(url, request, { params, responseType: 'blob' });
    }

    /**
     * Retrieves a sales order by its ID.
     * @param id The ID of the sales order.
     * @returns An Observable of a SalesOrder object.
     */
    getSalesOrderById(id: number): Observable<SalesOrder> {
        const url = `${this.baseUrl}/sales-orders/${id}`;
        return this.http.get<SalesOrder>(url);
    }

    previewExecutionDraft(request: ExecutionDraftPreviewRequest): Observable<ExecutionDraftPreview> {
        return this.http.post<ExecutionDraftPreview>(`${this.baseUrl}/sales-orders/execution-drafts/preview`, request);
    }

    createExecutionDraft(request: ExecutionDraftCreateRequest): Observable<ExecutionDraftCreateResponse> {
        return this.http.post<ExecutionDraftCreateResponse>(`${this.baseUrl}/sales-orders/execution-drafts`, request);
    }

    previewExecutionImport(orderId: number, request: ExecutionDraftPreviewRequest): Observable<ExecutionDraftPreview> {
        return this.http.post<ExecutionDraftPreview>(`${this.baseUrl}/sales-orders/${orderId}/execution-lines/preview`, request);
    }

    importExecutionLines(orderId: number, request: ExecutionDraftCreateRequest): Observable<SalesOrder> {
        return this.http.post<SalesOrder>(`${this.baseUrl}/sales-orders/${orderId}/execution-lines/import`, request);
    }

    getExecutionLineage(orderId: number, owningCompanyId: number): Observable<ExecutionOrderLineage> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId);
        return this.http.get<ExecutionOrderLineage>(`${this.baseUrl}/sales-orders/${orderId}/execution-lineage`, { params });
    }

    getSalesOrderActivity(id: number): Observable<OrderActivity[]> {
        const url = `${this.baseUrl}/sales-orders/${id}/activity`;
        return this.http.get<OrderActivity[]>(url);
    }

    getSalesOrderActions(id: number): Observable<SalesOrderActionMetadata> {
        const url = `${this.baseUrl}/sales-orders/${id}/actions`;
        return this.http.get<SalesOrderActionMetadata>(url);
    }

    previewSalesOrderFromQuotation(quotationId: number, companyId: number, request: SalesOrderFromQuotationRequest = {}): Observable<SalesOrderFromQuotationPreview> {
        const url = `${this.baseUrl}/sales-orders/from-quotation/${quotationId}/preview`;
        let params = new HttpParams().set('companyId', companyId.toString());
        if (request.customerId) params = params.set('customerId', request.customerId.toString());
        if (request.logisticsProjectId) params = params.set('logisticsProjectId', request.logisticsProjectId.toString());
        if (request.paymentTermId) params = params.set('paymentTermId', request.paymentTermId.toString());
        if (request.actorId) params = params.set('actorId', request.actorId.toString());
        if (request.overrideReason) params = params.set('overrideReason', request.overrideReason);
        return this.http.get<SalesOrderFromQuotationPreview>(url, { params });
    }

    createSalesOrderFromQuotation(quotationId: number, companyId: number, request: SalesOrderFromQuotationRequest): Observable<SalesOrderFromQuotationResult> {
        const url = `${this.baseUrl}/sales-orders/from-quotation/${quotationId}`;
        const params = new HttpParams().set('companyId', companyId.toString());
        return this.http.post<SalesOrderFromQuotationResult>(url, request, { params });
    }

    getSalesOrdersBySourceQuotation(quotationId: number, companyId: number): Observable<SalesOrder[]> {
        const url = `${this.baseUrl}/sales-orders/source-quotation/${quotationId}`;
        const params = new HttpParams().set('companyId', companyId.toString());
        return this.http.get<SalesOrder[]>(url, { params });
    }

    /**
     * Creates a new sales order.
     * @param salesOrder The SalesOrder object to create.
     * @returns An Observable of the created SalesOrder object.
     */
    createSalesOrder(salesOrder: SalesOrder): Observable<SalesOrder> {
        const url = `${this.baseUrl}/sales-orders`;
        return this.http.post<SalesOrder>(url, salesOrder);
    }

    updateSalesOrder(salesOrderId: number, salesOrderData: SalesOrder) {
        const url = `${this.baseUrl}/sales-orders/${salesOrderId}`;
        return this.http.put<SalesOrder>(url, salesOrderData);
    }

    updateSalesOrderNotes(salesOrderId: number, notes: string, updatedBy: number): Observable<SalesOrder> {
        const url = `${this.baseUrl}/sales-orders/${salesOrderId}/notes`;
        return this.http.patch<SalesOrder>(url, { notes, updatedBy });
    }

    /**
     * Confirms a sales order.
     * @param salesOrder The SalesOrder object to create.
     * @returns An Observable of the created SalesOrder object.
     */
    confirmSalesOrder(salesOrder: SalesOrder): Observable<Boolean> {
        const url = `${this.baseUrl}/sales-orders/confirm`;
        return this.http.patch<Boolean>(url, salesOrder);
    }

    runSalesOrderAction(id: number, action: string, request: SalesOrderLifecycleActionRequest = {}): Observable<SalesOrder> {
        const url = `${this.baseUrl}/sales-orders/${id}/${action}`;
        return this.http.post<SalesOrder>(url, request);
    }

    /**
     * Deletes a sales order by its ID.
     * @param id The ID of the sales order to delete.
     * @returns An Observable of void.
     */
    deleteSalesOrder(id: number): Observable<void> {
        const url = `${this.baseUrl}/sales-orders/${id}`;
        return this.http.delete<void>(url);
    }

    /**
     * Retrieves all purchase orders.
     * @param owningCompanyId The ID of the owning company.
     * @param page The page number.
     * @param pageSize The number of items per page.
     * @returns An Observable of APIListWrapper<PurchaseOrder> object.
     */

    getAllPurchaseOrders(
        owningCompanyId: number,
        page: number,
        pageSize: number
    ): Observable<any> {
        const url = `${this.baseUrl}/purchase-orders`;
        const params = new HttpParams()
            .set('owningCompanyId', owningCompanyId.toString())
            .set('page', page.toString())
            .set('pageSize', pageSize.toString())
        return this.http.get<APIListWrapper<PurchaseOrder>>(url, { params });
    }

    searchPurchaseOrders(request: OrderSearchRequest): Observable<APIListWrapper<OrderListRow>> {
        const url = `${this.baseUrl}/purchase-orders/search`;
        return this.http.post<APIListWrapper<OrderListRow>>(url, request);
    }

    getPurchaseOrderSummary(request: OrderSearchRequest): Observable<OrderSummary> {
        const url = `${this.baseUrl}/purchase-orders/summary`;
        return this.http.post<OrderSummary>(url, request);
    }

    exportPurchaseOrders(request: OrderSearchRequest, format: 'xlsx' | 'csv' = 'xlsx'): Observable<Blob> {
        const url = `${this.baseUrl}/purchase-orders/export`;
        const params = new HttpParams().set('format', format);
        return this.http.post(url, request, { params, responseType: 'blob' });
    }

    /**
     * Retrieves a purchase order by its ID.
     * @param id The ID of the purchase order.
     * @returns An Observable of a PurchaseOrder object.
     */
    getPurchaseOrderById(id: number): Observable<PurchaseOrder> {
        const url = `${this.baseUrl}/purchase-orders/${id}`;
        return this.http.get<PurchaseOrder>(url);
    }

    getPurchaseOrderReceipts(id: number, companyId: number): Observable<PurchaseOrderReceipt[]> {
        const params = new HttpParams().set('companyId', companyId.toString());
        return this.http.get<PurchaseOrderReceipt[]>(`${this.baseUrl}/purchase-orders/${id}/receipts`, { params });
    }

    getPurchaseOrderReceipt(id: number, receiptId: number, companyId: number): Observable<PurchaseOrderReceipt> {
        const params = new HttpParams().set('companyId', companyId.toString());
        return this.http.get<PurchaseOrderReceipt>(`${this.baseUrl}/purchase-orders/${id}/receipts/${receiptId}`, { params });
    }

    createPurchaseOrderReceipt(id: number, companyId: number, receipt: PurchaseOrderReceipt): Observable<PurchaseOrderReceipt> {
        const params = new HttpParams().set('companyId', companyId.toString());
        return this.http.post<PurchaseOrderReceipt>(`${this.baseUrl}/purchase-orders/${id}/receipts`, receipt, { params });
    }

    updatePurchaseOrderReceipt(id: number, receiptId: number, companyId: number, receipt: PurchaseOrderReceipt): Observable<PurchaseOrderReceipt> {
        const params = new HttpParams().set('companyId', companyId.toString());
        return this.http.put<PurchaseOrderReceipt>(`${this.baseUrl}/purchase-orders/${id}/receipts/${receiptId}`, receipt, { params });
    }

    confirmPurchaseOrderReceipt(id: number, receiptId: number, companyId: number, confirmedBy: number): Observable<PurchaseOrderReceipt> {
        const params = new HttpParams().set('companyId', companyId.toString());
        return this.http.post<PurchaseOrderReceipt>(`${this.baseUrl}/purchase-orders/${id}/receipts/${receiptId}/confirm`, { confirmedBy }, { params });
    }

    cancelPurchaseOrderReceipt(id: number, receiptId: number, companyId: number): Observable<void> {
        const params = new HttpParams().set('companyId', companyId.toString());
        return this.http.delete<void>(`${this.baseUrl}/purchase-orders/${id}/receipts/${receiptId}`, { params });
    }

    getPurchaseOrderActivity(id: number): Observable<OrderActivity[]> {
        const url = `${this.baseUrl}/purchase-orders/${id}/activity`;
        return this.http.get<OrderActivity[]>(url);
    }

    getPurchaseOrderActions(id: number): Observable<PurchaseOrderActionMetadata> {
        const url = `${this.baseUrl}/purchase-orders/${id}/actions`;
        return this.http.get<PurchaseOrderActionMetadata>(url);
    }

    getPurchaseOrderVendorBillReadiness(id: number): Observable<VendorBillReadinessResponse> {
        return this.http.get<VendorBillReadinessResponse>(`${this.baseUrl}/purchase-orders/${id}/vendor-bill-readiness`);
    }

    createPurchaseOrderVendorBillDraft(id: number, request: CreateVendorBillDraftRequest): Observable<VendorBillDraftResponse> {
        return this.http.post<VendorBillDraftResponse>(`${this.baseUrl}/purchase-orders/${id}/create-vendor-bill-draft`, request);
    }

    stagePurchaseOrderForConsolidation(id: number, request: CreateVendorBillDraftRequest): Observable<PayableStagingResponse> {
        return this.http.post<PayableStagingResponse>(`${this.baseUrl}/purchase-orders/${id}/stage-for-consolidation`, request);
    }

    runPurchaseOrderAction(id: number, action: string, actorId?: number, reason?: string): Observable<PurchaseOrder> {
        const url = `${this.baseUrl}/purchase-orders/${id}/${action}`;
        let params = new HttpParams();
        if (actorId) params = params.set('actorId', actorId.toString());
        if (reason) params = params.set('reason', reason);
        return this.http.post<PurchaseOrder>(url, null, { params });
    }

    /**
     * Creates a new purchase order.
     * @param purchaseOrder The PurchaseOrder object to create.
     * @returns An Observable of the created PurchaseOrder object.
     */
    createPurchaseOrder(purchaseOrder: PurchaseOrder): Observable<PurchaseOrder> {
        const url = `${this.baseUrl}/purchase-orders`;
        return this.http.post<PurchaseOrder>(url, purchaseOrder);
    }

    updatePurchaseOrder(purchaseOrderId: number, purchaseOrderData: PurchaseOrder): Observable<PurchaseOrder> {
        const url = `${this.baseUrl}/purchase-orders/${purchaseOrderId}`;
        return this.http.put<PurchaseOrder>(url, purchaseOrderData);
    }

    updatePurchaseOrderNotes(purchaseOrderId: number, notes: string, updatedBy: number): Observable<PurchaseOrder> {
        const url = `${this.baseUrl}/purchase-orders/${purchaseOrderId}/notes`;
        return this.http.patch<PurchaseOrder>(url, { notes, updatedBy });
    }

    /**
     * Confirms a purchase order.
     * @param purchaseOrder The PurchaseOrder object to create.
     * @returns An Observable of the created PurchaseOrder object.
     */
    confirmPurchaseOrder(purchaseOrder: PurchaseOrder): Observable<PurchaseOrder> {
        const url = `${this.baseUrl}/purchase-orders/confirm`;
        return this.http.patch<PurchaseOrder>(url, purchaseOrder);
    }

    /**
     * Deletes a purchase order by its ID.
     * @param id The ID of the purchase order to delete.
     * @returns An Observable of void.
     */
    deletePurchaseOrder(id: number): Observable<void> {
        const url = `${this.baseUrl}/purchase-orders`;
        return this.http.delete<void>(`${url}/${id}`);
    }

    /**
     * Retrieves all payment terms.
     * @returns An Observable of a list of PaymentTerm objects.
     */
    getAllPaymentTerms(owningCompanyId?: number): Observable<PaymentTerm[]> {
        const url = `${this.baseUrl}/payment-terms`;
        const params = owningCompanyId ? new HttpParams().set('owningCompanyId', owningCompanyId.toString()) : undefined;
        return this.http.get<PaymentTerm[]>(url, { params });
    }

    /**
     * Retrieves a payment term by its ID.
     * @param id The ID of the payment term.
     * @returns An Observable of a PaymentTerm object.
     */
    getPaymentTermById(id: number): Observable<PaymentTerm> {
        const url = `${this.baseUrl}/payment-terms/${id}`;
        return this.http.get<PaymentTerm>(url);
    }

    createSalesOrderAttachment(orderId: number, owningCompanyId: number, attachment: SalesOrderAttachment): Observable<SalesOrderAttachment> {
        const url = `${this.baseUrl}/sales-orders/${orderId}/attachments/owning-company/${owningCompanyId}`;
        return this.http.post<SalesOrderAttachment>(url, attachment);
    }

    getSalesOrderAttachments(orderId: number): Observable<SalesOrderAttachment[]> {
        const url = `${this.baseUrl}/sales-orders/${orderId}/attachments`;
        return this.http.get<SalesOrderAttachment[]>(url);
    }

    getSalesOrderAttachmentById(attachmentId: number): Observable<SalesOrderAttachment> {
        const url = `${this.baseUrl}/sales-orders/attachments/${attachmentId}`;
        return this.http.get<SalesOrderAttachment>(url);
    }

    deleteSalesOrderAttachment(attachmentId: number, reason: string): Observable<void> {
        const url = `${this.baseUrl}/sales-orders/attachments/${attachmentId}`;
        return this.http.delete<void>(url, { params: { reason } });
    }

    createPurchaseOrderAttachment(
        purchaseOrderId: number,
        owningCompanyId: number,
        attachment: PurchaseOrderAttachment
    ): Observable<PurchaseOrderAttachment> {
        const url = `${this.baseUrl}/purchase-orders/${purchaseOrderId}/attachments/owning-company/${owningCompanyId}`;
        return this.http.post<PurchaseOrderAttachment>(url, attachment);
    }

    getPurchaseOrderAttachments(purchaseOrderId: number): Observable<PurchaseOrderAttachment[]> {
        const url = `${this.baseUrl}/purchase-orders/${purchaseOrderId}/attachments`;
        return this.http.get<PurchaseOrderAttachment[]>(url);
    }

    getPurchaseOrderAttachmentById(attachmentId: number): Observable<PurchaseOrderAttachment> {
        const url = `${this.baseUrl}/purchase-orders/attachments/${attachmentId}`;
        return this.http.get<PurchaseOrderAttachment>(url);
    }

    deletePurchaseOrderAttachment(attachmentId: number, reason: string): Observable<void> {
        const url = `${this.baseUrl}/purchase-orders/attachments/${attachmentId}`;
        return this.http.delete<void>(url, { params: { reason } });
    }

    // ---- Phase 5: Fulfillment & Invoice Readiness ----

    getFulfillmentReadiness(salesOrderId: number): Observable<FulfillmentReadinessResponse> {
        const url = `${this.baseUrl}/sales-orders/${salesOrderId}/fulfillment-readiness`;
        return this.http.get<FulfillmentReadinessResponse>(url);
    }

    retrySalesOrderAllocation(salesOrderId: number, lineIds: number[], actorId?: number): Observable<FulfillmentReadinessResponse> {
        const url = `${this.baseUrl}/sales-orders/${salesOrderId}/allocations/retry`;
        return this.http.post<FulfillmentReadinessResponse>(url, { lineIds, actorId });
    }

    createTransportRelease(salesOrderId: number, request: CreateReleaseRequest): Observable<ReleaseStatusResponse> {
        const url = `${this.baseUrl}/sales-orders/${salesOrderId}/transport-releases`;
        return this.http.post<ReleaseStatusResponse>(url, request);
    }

    cancelTransportRelease(salesOrderId: number, rootReleaseId: number, reason: string): Observable<ReleaseStatusResponse> {
        const url = `${this.baseUrl}/sales-orders/${salesOrderId}/transport-releases/${rootReleaseId}/cancel`;
        return this.http.post<ReleaseStatusResponse>(url, { reason });
    }

    previewSalesOrderQuantityCorrection(salesOrderId: number, lineId: number): Observable<SalesOrderQuantityCorrectionPreview> {
        return this.http.get<SalesOrderQuantityCorrectionPreview>(
            `${this.baseUrl}/sales-orders/${salesOrderId}/lines/${lineId}/quantity-correction`);
    }

    applySalesOrderQuantityCorrection(salesOrderId: number, lineId: number,
        request: SalesOrderQuantityCorrectionRequest): Observable<SalesOrderQuantityCorrectionResult> {
        return this.http.post<SalesOrderQuantityCorrectionResult>(
            `${this.baseUrl}/sales-orders/${salesOrderId}/lines/${lineId}/quantity-correction`, request);
    }

    createReturnRelease(salesOrderId: number, request: CreateReturnReleaseRequest): Observable<ReleaseStatusResponse> {
        const url = `${this.baseUrl}/sales-orders/${salesOrderId}/return-releases`;
        return this.http.post<ReleaseStatusResponse>(url, request);
    }

    getTransportReleases(salesOrderId: number): Observable<ReleaseStatusResponse> {
        const url = `${this.baseUrl}/sales-orders/${salesOrderId}/transport-releases`;
        return this.http.get<ReleaseStatusResponse>(url);
    }

    syncFulfillment(salesOrderId: number): Observable<void> {
        const url = `${this.baseUrl}/sales-orders/${salesOrderId}/sync-fulfillment`;
        return this.http.post<void>(url, {});
    }

    getInvoiceReadiness(salesOrderId: number): Observable<InvoiceReadinessResponse> {
        const url = `${this.baseUrl}/sales-orders/${salesOrderId}/invoice-readiness`;
        return this.http.get<InvoiceReadinessResponse>(url);
    }

    createInvoiceDraft(salesOrderId: number, request: CreateInvoiceDraftRequest): Observable<InvoiceDraftResponse> {
        const url = `${this.baseUrl}/sales-orders/${salesOrderId}/create-invoice-draft`;
        return this.http.post<InvoiceDraftResponse>(url, request);
    }

    stageSalesOrderForConsolidation(salesOrderId: number, request: CreateInvoiceDraftRequest): Observable<InvoiceDraftResponse> {
        return this.http.post<InvoiceDraftResponse>(`${this.baseUrl}/sales-orders/${salesOrderId}/stage-for-consolidation`, request);
    }

    getOrderSavedViews(module: 'SALES_ORDER' | 'PURCHASE_ORDER', owningCompanyId: number, userId: number): Observable<OrderSavedView[]> {
        const url = `${this.baseUrl}/saved-views`;
        const params = new HttpParams()
            .set('module', module)
            .set('owningCompanyId', owningCompanyId.toString())
            .set('userId', userId.toString());
        return this.http.get<OrderSavedView[]>(url, { params });
    }

    createOrderSavedView(savedView: OrderSavedView): Observable<OrderSavedView> {
        const url = `${this.baseUrl}/saved-views`;
        return this.http.post<OrderSavedView>(url, savedView);
    }

    updateOrderSavedView(savedViewId: number, savedView: OrderSavedView): Observable<OrderSavedView> {
        const url = `${this.baseUrl}/saved-views/${savedViewId}`;
        return this.http.put<OrderSavedView>(url, savedView);
    }

    deleteOrderSavedView(savedViewId: number): Observable<void> {
        const url = `${this.baseUrl}/saved-views/${savedViewId}`;
        return this.http.delete<void>(url);
    }
}
